import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiClock } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';

import { clearDashboardSummary, fetchDashboardSummary } from '../../store/dashboardSlice';
import { quickActionRoutes, reportRoutes } from '../../routesConfig';
import './Dashboard.css';

const formatAmount = (value) => `₹ ${Number(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const Dashboard = () => {
  const dispatch = useDispatch();
  const { clientId, financialYear } = useSelector((state) => state.app);
  const { summary, loading, error } = useSelector((state) => state.dashboard);

  useEffect(() => {
    if (!clientId || !financialYear) {
      dispatch(clearDashboardSummary());
      return;
    }
    dispatch(fetchDashboardSummary());
  }, [clientId, dispatch, financialYear]);

  const totalTaxableCapitalGains = useMemo(
    () => Number(summary.taxableShortTermCapitalGain ?? 0) + Number(summary.taxableLongTermCapitalGain ?? 0),
    [summary.taxableLongTermCapitalGain, summary.taxableShortTermCapitalGain]
  );

  const primaryCards = [
    { label: 'Total Revenue', value: formatAmount(summary.totalRevenue) },
    { label: 'Gross Total Income', value: formatAmount(summary.grossTotalIncome), accent: true },
    { label: 'Total Assets', value: formatAmount(summary.totalAssets) },
    { label: 'Taxable Capital Gains', value: formatAmount(totalTaxableCapitalGains) },
  ];

  const workCards = [
    {
      label: 'Income Entries',
      value: summary.incomeEntryCount,
      detail: 'Manual income rows available for tax reporting.',
      icon: FiCheckCircle,
    },
    {
      label: 'Expense Entries',
      value: summary.expenseEntryCount,
      detail: 'Operational and indirect expenses in the current dataset.',
      icon: FiCheckCircle,
    },
    {
      label: 'Asset Masters',
      value: summary.assetCount,
      detail: 'Assets driving both accounting and tax depreciation.',
      icon: FiCheckCircle,
    },
    {
      label: 'Delivery Trade Review',
      value: summary.deliveryTradeCount,
      detail: `${summary.deliveryInvestmentCount} investment and ${summary.deliveryBusinessCount} business classifications recorded.`,
      icon: FiClock,
    },
  ];

  const reportHighlights = [
    { path: '/profit-loss', value: formatAmount(summary.totalRevenue), helper: 'Revenue from the P&L statement.' },
    { path: '/balance-sheet', value: formatAmount(summary.totalAssets), helper: 'Closing asset position from the balance sheet.' },
    { path: '/capital-gain', value: formatAmount(summary.longTermCapitalGainGross), helper: 'Gross LTCG before 112A relief.' },
    { path: '/tax-computation', value: formatAmount(summary.businessIncomeAfterTaxDepreciation), helper: 'Business income after tax depreciation.' },
  ];

  if (!clientId || !financialYear) {
    return (
      <div className="dashboard-page">
        <section className="dashboard-hero dashboard-hero--empty">
          <div>
            <p className="dashboard-eyebrow">Finance Workspace</p>
            <h1>Start With Context</h1>
            <p>Select a client and financial year from the top bar to load summary totals, work queues, and report shortcuts.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <section className="dashboard-hero">
        <div>
          <p className="dashboard-eyebrow">Professional Finance Workspace</p>
          <h1>Accounting Dashboard</h1>
          <p>Track reporting totals, tax posture, and pending classification work without jumping across every module first.</p>
        </div>
        <div className="dashboard-context-card">
          <div className="dashboard-context-card__row">
            <span>Client</span>
            <strong>{summary.clientId || clientId}</strong>
          </div>
          <div className="dashboard-context-card__row">
            <span>Financial Year</span>
            <strong>{summary.financialYear || financialYear}</strong>
          </div>
          <div className="dashboard-context-card__row">
            <span>Tax Depreciation</span>
            <strong>{formatAmount(summary.taxDepreciation)}</strong>
          </div>
        </div>
      </section>

      {loading && <p className="financial-message">Loading dashboard summary...</p>}
      {error && <p className="financial-message financial-message--error">{error}</p>}

      <section className="dashboard-stat-grid">
        {primaryCards.map((card) => (
          <article key={card.label} className={`dashboard-stat-card${card.accent ? ' dashboard-stat-card--accent' : ''}`}>
            <span className="dashboard-stat-card__label">{card.label}</span>
            <strong>{card.value}</strong>
          </article>
        ))}
      </section>

      <section className="dashboard-work-grid">
        {workCards.map((card) => {
          const Icon = card.icon;
          return (
            <article key={card.label} className="dashboard-work-card">
              <div className="dashboard-work-card__icon"><Icon /></div>
              <div>
                <span className="dashboard-work-card__label">{card.label}</span>
                <strong>{card.value}</strong>
                <p>{card.detail}</p>
              </div>
            </article>
          );
        })}
      </section>

      <section className="dashboard-panel-grid">
        <article className="dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <h2>Quick Actions</h2>
              <p>Jump straight into the workflows that usually need updates during a close or review cycle.</p>
            </div>
          </div>
          <div className="dashboard-action-grid">
            {quickActionRoutes.map((route) => {
              const Icon = route.icon;
              return (
                <Link key={route.path} to={route.path} className="dashboard-action-link">
                  <div className="dashboard-action-link__icon"><Icon /></div>
                  <div>
                    <strong>{route.label}</strong>
                    <span>{route.description}</span>
                  </div>
                  <FiArrowRight className="dashboard-action-link__arrow" />
                </Link>
              );
            })}
          </div>
        </article>

        <article className="dashboard-panel">
          <div className="dashboard-panel__header">
            <div>
              <h2>Report Highlights</h2>
              <p>Move from the summary view into the detailed report that supports each headline number.</p>
            </div>
          </div>
          <div className="dashboard-report-list">
            {reportHighlights.map((item) => {
              const route = reportRoutes.find((candidate) => candidate.path === item.path);
              if (!route) {
                return null;
              }
              const Icon = route.icon;
              return (
                <Link key={route.path} to={route.path} className="dashboard-report-row">
                  <div className="dashboard-report-row__meta">
                    <div className="dashboard-report-row__icon"><Icon /></div>
                    <div>
                      <strong>{route.label}</strong>
                      <span>{item.helper}</span>
                    </div>
                  </div>
                  <div className="dashboard-report-row__value">{item.value}</div>
                </Link>
              );
            })}
          </div>
        </article>
      </section>
    </div>
  );
};

export default Dashboard;


